// JobClose AI · Mohit QA Reporting V1 · Admin engineering surface.
// Sibling of /app/admin/email-outbox. Gated by <AdminGuard>.
// Provides list, drawer, and engineering transitions (response,
// ready-for-retest, close, reopen).
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api } from "@/lib/api";
import { Loader2, RefreshCw, Check, RotateCcw, X } from "lucide-react";

const STATUS_ORDER = [
  "new", "in_progress", "ready_for_retest", "deferred",
  "closed_fixed", "closed_wont_fix", "closed_not_a_bug",
];

export default function AdminQaFindings() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ engineering_status: "", severity: "" });
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (filter.engineering_status) q.append("engineering_status", filter.engineering_status);
      if (filter.severity) q.append("severity", filter.severity);
      const { data } = await api.get(`/qa/findings?${q.toString()}`);
      setRows(data.findings || []);
    } catch (e) {
      toast.error("Couldn't load findings.");
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  return (
    <DashboardLayout title="QA Findings (Admin)"
      subtitle="Engineering triage · response · retest · close · reopen">
      <div data-testid="admin-qa-findings-page" className="space-y-4">
        <div className="flex items-center gap-2">
          <select className="jc-input max-w-[220px]"
                  data-testid="admin-qa-filter-status"
                  value={filter.engineering_status}
                  onChange={(e) => setFilter((f) => ({ ...f, engineering_status: e.target.value }))}>
            <option value="">All statuses</option>
            {STATUS_ORDER.map((s) => (<option key={s} value={s}>{s}</option>))}
          </select>
          <select className="jc-input max-w-[140px]"
                  data-testid="admin-qa-filter-severity"
                  value={filter.severity}
                  onChange={(e) => setFilter((f) => ({ ...f, severity: e.target.value }))}>
            <option value="">All severity</option>
            {["P0","P1","P2","P3"].map((s) => (<option key={s} value={s}>{s}</option>))}
          </select>
          <button onClick={load} data-testid="admin-qa-refresh"
                  className="ml-auto inline-flex items-center gap-1 text-xs px-2 py-1.5 rounded border">
            <RefreshCw size={12} /> Refresh
          </button>
        </div>

        {loading ? (
          <div className="p-6 flex items-center gap-2 text-sm">
            <Loader2 className="animate-spin" size={14} /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-8 border border-dashed rounded text-center text-sm text-[var(--jc-text-soft)]"
               data-testid="admin-qa-empty">
            No findings match this filter.
          </div>
        ) : (
          <div className="jc-card overflow-x-auto" data-testid="admin-qa-list">
            <table className="w-full text-sm">
              <thead className="bg-[var(--jc-bg)] text-left text-xs text-[var(--jc-text-soft)]">
                <tr>
                  <th className="px-3 py-2">Result</th>
                  <th className="px-3 py-2">Test / Area</th>
                  <th className="px-3 py-2">QA user</th>
                  <th className="px-3 py-2">Tenant</th>
                  <th className="px-3 py-2">Severity</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Updated</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((f) => (
                  <tr key={f.id} data-testid={`admin-qa-row-${f.id}`}
                      onClick={() => setOpen(f)}
                      className="border-t border-[var(--jc-border)] hover:bg-[var(--jc-bg)] cursor-pointer">
                    <td className="px-3 py-2">{f.result}</td>
                    <td className="px-3 py-2">
                      <div className="font-semibold">{f.test_id || "—"}</div>
                      <div className="text-xs text-[var(--jc-text-soft)]">{f.area || "—"}</div>
                    </td>
                    <td className="px-3 py-2 text-xs">{f.qa_user_email || "—"}</td>
                    <td className="px-3 py-2 text-xs">{f.tenant_label || f.organization_id?.slice(0, 8)}</td>
                    <td className="px-3 py-2 text-xs">{f.severity || "—"}</td>
                    <td className="px-3 py-2 text-xs">{f.engineering_status}</td>
                    <td className="px-3 py-2 text-xs">{f.updated_at?.slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {open && (
        <AdminDrawer finding={open}
                     onClose={() => setOpen(null)}
                     onChanged={() => { load(); setOpen(null); }} />
      )}
    </DashboardLayout>
  );
}

function AdminDrawer({ finding, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [respText, setRespText] = useState("");
  const [saving, setSaving] = useState(false);
  const load = useCallback(async () => {
    const { data } = await api.get(`/qa/findings/${finding.id}`);
    setDetail(data);
    setRespText(data?.finding?.engineering_response_text || "");
  }, [finding.id]);
  useEffect(() => { load(); }, [load]);

  const call = async (verb, body) => {
    setSaving(true);
    try {
      await api.post(`/qa/findings/${finding.id}/${verb}`, body || {});
      toast.success(`Applied: ${verb}`);
      onChanged?.();
    } catch (e) {
      toast.error(`Failed: ${verb}`);
    } finally { setSaving(false); }
  };

  const saveResponse = () => call("response", {
    engineering_response_text: respText,
    status_transition: "in_progress",
  });

  if (!finding) return null;
  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex justify-end"
         data-testid="admin-qa-drawer" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white h-full overflow-y-auto p-6 space-y-4"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold flex-1" data-testid="admin-qa-drawer-title">
            {finding.test_id || "Finding"} · {finding.result} · {finding.engineering_status}
          </h2>
          <button onClick={onClose} className="text-sm">Close</button>
        </div>
        <div className="text-xs text-[var(--jc-text-soft)]">
          Reporter: {finding.qa_user_email} · Tenant: {finding.tenant_label || finding.organization_id}
        </div>
        <ReadOnly label="Steps" value={finding.exact_steps} />
        <ReadOnly label="Expected" value={finding.expected_result} />
        <ReadOnly label="Actual" value={finding.actual_result} />

        <div className="space-y-1">
          <div className="text-xs font-semibold">Engineering response</div>
          <textarea rows={4} className="jc-input" value={respText}
                    data-testid="admin-qa-response-text"
                    onChange={(e) => setRespText(e.target.value)} />
          <div className="flex items-center gap-2">
            <button disabled={saving} onClick={saveResponse}
                    data-testid="admin-qa-response-save"
                    className="text-xs px-3 py-1.5 rounded jc-btn-blue font-semibold">
              Save response
            </button>
            <button disabled={saving} onClick={() => call("ready-for-retest")}
                    data-testid="admin-qa-mark-ready"
                    className="text-xs px-3 py-1.5 rounded bg-amber-500 text-white font-semibold">
              Mark ready for retest
            </button>
            <button disabled={saving} onClick={() => call("close", { closure: "fixed" })}
                    data-testid="admin-qa-close-fixed"
                    className="text-xs px-3 py-1.5 rounded bg-emerald-600 text-white font-semibold inline-flex items-center gap-1">
              <Check size={12} /> Close (fixed)
            </button>
            <button disabled={saving} onClick={() => call("close", { closure: "wont_fix" })}
                    data-testid="admin-qa-close-wontfix"
                    className="text-xs px-3 py-1.5 rounded border font-semibold inline-flex items-center gap-1">
              <X size={12} /> Won&rsquo;t fix
            </button>
            <button disabled={saving} onClick={() => call("reopen")}
                    data-testid="admin-qa-reopen"
                    className="text-xs px-3 py-1.5 rounded border font-semibold inline-flex items-center gap-1">
              <RotateCcw size={12} /> Reopen
            </button>
          </div>
        </div>

        <div>
          <div className="text-xs font-semibold mb-1">Event trail</div>
          <ul className="text-xs space-y-1" data-testid="admin-qa-events">
            {(detail?.events || []).map((e) => (
              <li key={e.id} className="border-l-2 border-[var(--jc-border)] pl-2">
                <span className="font-semibold">{e.event_type}</span>
                <span className="text-[var(--jc-text-soft)]"> · {e.new_status || "—"} · {e.actor_role} · {e.created_at?.slice(0, 16)}</span>
                {e.text && <div className="mt-0.5">{e.text}</div>}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function ReadOnly({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <div className="text-xs font-semibold text-[var(--jc-text-soft)]">{label}</div>
      <div className="text-sm whitespace-pre-wrap border rounded p-2 bg-[var(--jc-bg)]">
        {value}
      </div>
    </div>
  );
}
